import type { BillingAddress } from '../types';
import type { BillingInfo, PaymentMethodCode } from '../data/paymentRepository';

/**
 * 請求先住所フォームの状態
 */
export interface BillingAddressForm {
  zipCode: string;
  addressPrefectureId: string;
  addressCity: string;
  addressDetail: string;
  addressBuilding: string;
}

/**
 * 請求書払いの請求先情報入力
 */
export interface InvoiceBillingInput {
  billingTypeId: string;
  billingName: string;
  addressForm: BillingAddressForm;
}

/**
 * BillingAddressForm の初期値
 */
export const INITIAL_BILLING_ADDRESS_FORM: BillingAddressForm = {
  zipCode: '',
  addressPrefectureId: '',
  addressCity: '',
  addressDetail: '',
  addressBuilding: '',
};

/**
 * 請求書払いの必須項目が入力されているか判定
 *
 * 請求先区分・請求先名・住所（建物名以外）が必須。
 * 顧客区分（法人・個人）による差は無い（2026-08-08に法人の入力スキップを撤廃。ドクター #153 相当）。
 */
export const isInvoiceBillingValid = ({
  billingTypeId,
  billingName,
  addressForm,
}: InvoiceBillingInput): boolean => {
  if (!billingTypeId.trim()) return false;
  return (
    !!billingName.trim() &&
    !!addressForm.zipCode.trim() &&
    !!addressForm.addressPrefectureId.trim() &&
    !!addressForm.addressCity.trim() &&
    !!addressForm.addressDetail.trim()
  );
};

/**
 * 請求書払いのリクエスト用請求先情報を構築
 * - 請求先区分・請求先名・住所を全て含める（顧客区分による除外なし）
 */
export const buildInvoiceBillingAddress = ({
  billingTypeId,
  billingName,
  addressForm,
}: InvoiceBillingInput): BillingAddress => {
  return {
    billingTypeId,
    name: billingName.trim(),
    zipCode: addressForm.zipCode.trim(),
    addressPrefectureId: addressForm.addressPrefectureId.trim(),
    addressCity: addressForm.addressCity.trim(),
    addressDetail: addressForm.addressDetail.trim(),
    addressBuilding: addressForm.addressBuilding.trim(),
  };
};

/**
 * 修繕履歴詳細で請求先情報Boxを表示するか判定
 *
 * 作業完了 + 請求書払い + billing情報あり で表示する。
 * 顧客区分（法人・個人）は問わない（2026-08-08に個人限定の条件を撤廃）。
 */
export const shouldShowBillingInfo = ({
  status,
  paymentMethodCode,
  billing,
}: {
  status: string | undefined;
  paymentMethodCode: PaymentMethodCode | null | undefined;
  billing: BillingInfo | null | undefined;
}): boolean => {
  return status === '作業完了' && paymentMethodCode === 'bill' && billing != null;
};
